/** @file AngleField: a degree field (label scrubs) plus a slider (-180..180); drags preview, release commits. */
import type { ReactElement } from 'react';
import { NumericField } from '../../ui-kit/numericInput/NumericField';
import { signedDegrees } from './signedDegrees';

/** The angle in degrees, the field's hover text, whether it is inherited (muted) or locked, the change callback (`final` on release) and the scrub cancel. */
export type AngleFieldProps = {
  label: string; value: number; muted: boolean; disabled: boolean; title?: string;
  onChange: (deg: number, final: boolean) => void; onCancel?: () => void;
};

/**
 * Slider input events call `onChange(v, false)` (the caller previews through `poseOverride`); pointer release, lost
 * capture and blur call `onChange(v, true)` (the caller commits once if a preview is pending). The number field commits
 * directly and its label scrubs (preview per step, one commit on release, Esc calls `onCancel`). The unit is in the label.
 */
export function AngleField(props: AngleFieldProps): ReactElement {
  const { label, value, onChange } = props;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 128, flex: 'none', display: 'flex' }}>
        <NumericField label={label} title={props.title} value={Math.round(value * 100) / 100} muted={props.muted} disabled={props.disabled}
          handle={<span style={{ display: 'inline-block', width: 62, textAlign: 'left' }}>{label} (deg)</span>}
          onPreview={(v) => { onChange(signedDegrees(v), false); }} onCancel={props.onCancel}
          onCommit={(v) => { onChange(signedDegrees(v), false); onChange(signedDegrees(v), true); }} />
      </div>
      <input type="range" aria-label={`${label} slider`} data-field={`${label} slider`} min={-180} max={180} step={1} value={value} disabled={props.disabled}
        style={{ flex: 1, minWidth: 60, accentColor: 'var(--p3-accent, #f2b544)' }}
        onChange={(e) => { onChange(e.currentTarget.valueAsNumber, false); }}
        onPointerUp={(e) => { onChange(e.currentTarget.valueAsNumber, true); }}
        onLostPointerCapture={(e) => { onChange(e.currentTarget.valueAsNumber, true); }}
        onBlur={(e) => { onChange(e.currentTarget.valueAsNumber, true); }} />
    </div>
  );
}
